import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import {
  ActivateCodeResponse,
  RegisterUserResponse,
} from '@tsin-core/models/guest.model';
import { GuestService } from '@tsin-core/services/http/guest.service';
import { catchError, tap } from 'rxjs';
import { ActivateCode, RegisterUser } from './guest.actions';

export interface GuestStateModel {
  activation: ActivateCodeResponse | null;
  registration: RegisterUserResponse | null;
  loading: LoadingState;
  error: string | null;
}

@State<GuestStateModel>({
  name: 'guestState',
  defaults: {
    activation: null,
    registration: null,
    loading: LoadingState.notLoading,
    error: null,
  },
})
@Injectable()
export class GuestState {
  private guestService: GuestService = inject(GuestService);

  @Selector()
  static getActivation(state: GuestStateModel): ActivateCodeResponse | null {
    return state.activation;
  }

  @Selector()
  static getRegistration(state: GuestStateModel): RegisterUserResponse | null {
    return state.registration;
  }

  @Selector()
  static getLoading(state: GuestStateModel): LoadingState {
    return state.loading;
  }

  @Selector()
  static getError(state: GuestStateModel): string | null {
    return state.error;
  }

  @Action(ActivateCode)
  activateCode(ctx: StateContext<GuestStateModel>, action: ActivateCode) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate, error: null });
    return this.guestService.activate(action.payload).pipe(
      tap((activation: ActivateCodeResponse) => {
        ctx.patchState({ activation, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingState.notLoading,
          error: error?.error?.message ?? error?.message ?? 'Activation failed',
        });
        throw error;
      })
    );
  }

  @Action(RegisterUser)
  registerUser(ctx: StateContext<GuestStateModel>, action: RegisterUser) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate, error: null });
    return this.guestService.register(action.payload).pipe(
      tap((registration: RegisterUserResponse) => {
        ctx.patchState({ registration, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingState.notLoading,
          error:
            error?.error?.message ?? error?.message ?? 'Registration failed',
        });
        throw error;
      })
    );
  }
}
